import React, { useState } from 'react';
import { Loading } from '@appkit4/react-components/loading';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Checkbox } from '@appkit4/react-components/checkbox';
import DropDown from '../../../components/reusables/DropDown';
import './SetupModal.scss';

interface JurisdictionOption {
    value: string;
    label: string;
}

interface CreateEntityModalProps {
    jurisdictionOptions: JurisdictionOption[];
    isCreating: boolean;
    visible: boolean;
    onSubmit: (data: {
        entityDisplayName: string;
        companyId: string;
        jurisdictionId: number;
        rollover: boolean;
    }) => void;
}

const CreateEntityModal: React.FC<CreateEntityModalProps> = ({
    jurisdictionOptions,
    isCreating,
    visible,
    onSubmit,
}) => {
    const [entityName, setEntityName] = useState('');
    const [entityCompanyId, setEntityCompanyId] = useState('');
    const [entityJurisdictionId, setEntityJurisdictionId] = useState<
        string | undefined
    >();
    const [entityRollover, setEntityRollover] = useState(false);

    const handleSubmit = () => {
        if (!entityName || !entityCompanyId || !entityJurisdictionId) return;
        onSubmit({
            entityDisplayName: entityName,
            companyId: entityCompanyId,
            jurisdictionId: parseInt(entityJurisdictionId, 10),
            rollover: entityRollover,
        });
        setEntityName('');
        setEntityCompanyId('');
        setEntityJurisdictionId(undefined);
        setEntityRollover(false);
    };

    if (!visible) return null;

    return (
        <div className="setup-modal">
            <div className="setup-modal__backdrop" />
            <div
                role="dialog"
                aria-label="Create Your First Entity"
                className="setup-modal__dialog"
            >
                <h2 className="setup-modal__title">Create Your First Entity</h2>
                <div className="setup-modal__body">
                    {isCreating ? (
                        <Loading loadingType="circular" indeterminate />
                    ) : (
                        <>
                            <p className="setup-modal__text">
                                Before you can start working, you need to create
                                an entity for this engagement.
                            </p>
                            <Input
                                title="Company ID"
                                value={entityCompanyId}
                                onChange={(value: string) =>
                                    setEntityCompanyId(value)
                                }
                                required
                            />
                            <Input
                                title="Entity Name"
                                value={entityName}
                                onChange={(value: string) =>
                                    setEntityName(value)
                                }
                                required
                            />
                            <DropDown
                                data={jurisdictionOptions}
                                placeholder="Select Jurisdiction"
                                value={entityJurisdictionId}
                                onSelect={(value) => {
                                    if (typeof value === 'string') {
                                        setEntityJurisdictionId(value);
                                    }
                                }}
                            />
                            <Checkbox
                                checked={entityRollover}
                                onChange={(value: boolean) =>
                                    setEntityRollover(value)
                                }
                            >
                                Rollover Rules
                            </Checkbox>
                            <Button
                                onClick={handleSubmit}
                                kind="primary"
                                disabled={
                                    !entityName ||
                                    !entityCompanyId ||
                                    !entityJurisdictionId
                                }
                            >
                                Create Entity
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateEntityModal;
